/**
 * Secure Token Storage Utilities
 * 
 * This module provides secure alternatives to sessionStorage for JWT tokens
 * to prevent XSS attacks and improve security.
 */

// Interface for token storage
export interface TokenStorage {
  accessToken: string | null;
  idToken: string | null;
  refreshToken: string | null;
}

// In-memory storage for access tokens (cleared on page refresh)
let memoryTokenStorage: TokenStorage = {
  accessToken: null,
  idToken: null,
  refreshToken: null,
};

/**
 * Set tokens using secure storage methods
 * - Access tokens: In-memory only (cleared on refresh)
 * - ID tokens: In-memory only (cleared on refresh)  
 * - Refresh tokens: httpOnly cookies (handled by backend)
 */
export const setSecureTokens = (tokens: {
  accessToken?: string;
  idToken?: string;
  refreshToken?: string;
}) => {
  // Store access and ID tokens in memory only
  if (tokens.accessToken !== undefined) {
    memoryTokenStorage.accessToken = tokens.accessToken;
  }
  
  if (tokens.idToken !== undefined) {
    memoryTokenStorage.idToken = tokens.idToken;
  }

  // For refresh tokens, we'll send them to backend to set as httpOnly cookies
  if (tokens.refreshToken !== undefined) {
    // This will be handled by the backend when we call the login endpoint
    // The backend should set the refresh token as an httpOnly cookie
    memoryTokenStorage.refreshToken = tokens.refreshToken;
    
    // Call backend to set httpOnly cookie
    if (tokens.refreshToken) {
      setRefreshTokenCookie(tokens.refreshToken);
    }
  }
};

/**
 * Get tokens from secure storage
 */
export const getSecureTokens = (): TokenStorage => {
  return { ...memoryTokenStorage };
};

/**
 * Get specific token from secure storage
 */
export const getSecureToken = (tokenType: keyof TokenStorage): string | null => {
  return memoryTokenStorage[tokenType];
};

/**
 * Clear all tokens from secure storage
 */
export const clearSecureTokens = () => {
  memoryTokenStorage = {
    accessToken: null,
    idToken: null,
    refreshToken: null,
  };
  
  // Clear refresh token cookie via backend
  clearRefreshTokenCookie();
};

/**
 * Check if user is authenticated (has valid access token)
 */
export const isAuthenticated = (): boolean => {
  return memoryTokenStorage.accessToken !== null;
};

/**
 * Send refresh token to backend to be set as httpOnly cookie
 */
const setRefreshTokenCookie = async (refreshToken: string) => {
  try {
    await fetch('/api/auth/set-refresh-cookie', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Important for cookies
      body: JSON.stringify({ refreshToken }),
    });
  } catch (error) {
    console.error('Failed to set refresh token cookie:', error);
  }
};

/**
 * Clear refresh token cookie via backend
 */
const clearRefreshTokenCookie = async () => {
  try {
    await fetch('/api/auth/clear-refresh-cookie', {
      method: 'POST',
      credentials: 'include', // Important for cookies
    });
  } catch (error) {
    console.error('Failed to clear refresh token cookie:', error);
  }
};

/**
 * Migration utility: Move tokens from sessionStorage to secure storage
 * This helps with the transition from insecure to secure storage
 */
export const migrateFromSessionStorage = () => {
  try {
    const accessToken = sessionStorage.getItem('accessToken');
    const idToken = sessionStorage.getItem('idToken');
    const refreshToken = sessionStorage.getItem('refreshToken');

    if (accessToken || idToken || refreshToken) {
      console.log('Migrating tokens from sessionStorage to secure storage...');
      
      setSecureTokens({
        accessToken: accessToken || undefined,
        idToken: idToken || undefined,
        refreshToken: refreshToken || undefined,
      });

      // Clear old insecure storage
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('idToken');
      sessionStorage.removeItem('refreshToken');
      
      console.log('Token migration completed successfully');
    }
  } catch (error) {
    console.error('Failed to migrate tokens from sessionStorage:', error);
  }
};

/**
 * Initialize secure token storage
 * Call this when the app starts to handle migration and setup
 */
export const initializeSecureStorage = () => {
  // Migrate any existing tokens from sessionStorage
  migrateFromSessionStorage();
  
  // Set up visibility change listener to clear tokens when tab is hidden for security
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      // Optional: Clear sensitive tokens when tab becomes hidden
      // This adds extra security but may impact UX
      // Uncomment if needed:
      // clearSecureTokens();
    }
  });
};

export default {
  setSecureTokens,
  getSecureTokens,
  getSecureToken,
  clearSecureTokens,
  isAuthenticated,
  migrateFromSessionStorage,
  initializeSecureStorage,
};